import { useState } from 'react';
import styled from 'styled-components';

import { SETUP_INSTRUCTION_TYPE_LABELS } from 'pages/SiteSetups/constants/types';

import {
    GetMicrostoreProductsQuery,
    GetSiteSetupQuery,
    HotDrinksInstructionFragmentFragment,
    MicrostoreColumnInventoryFragmentFragment,
    MicrostoreColumnSetupFragmentFragment,
    MicrostoreColumnWithShelvesFragmentFragment,
    RemoveProductsInstructionFragmentFragment,
    SetupInstructionType,
} from 'data/__generated__';

import { dateFromString } from 'helpers/dateTimes';
import { getWeekdateBeforeNextSetup } from 'pages/SiteSetups/utils/getWeekdateBeforeNextSetup';
import { InventoryColumnDisplay } from './InventoryColumnDisplay';
import { InventoryProductsSection } from './InventoryProductsSection';
import { LocationDetailsSection } from './LocationDetailsSection';

export const ColumnInventorySection = ({
    column,
    columnInventory,
    columnSetup,
    inventoryDate,
    products,
    site,
    removeProductsInstruction,
    hotDrinksInstruction,
    selectedLocationId: externalSelectedLocationId,
    setSelectedLocationId: externalSetSelectedLocationId,
    siteSetupDate,
}: {
    column: MicrostoreColumnWithShelvesFragmentFragment;
    columnInventory: MicrostoreColumnInventoryFragmentFragment;
    columnSetup: MicrostoreColumnSetupFragmentFragment;
    inventoryDate: string;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    site: GetSiteSetupQuery['getSiteSetup']['site'];
    removeProductsInstruction?: RemoveProductsInstructionFragmentFragment;
    hotDrinksInstruction?: HotDrinksInstructionFragmentFragment;
    selectedLocationId?: string | null;
    setSelectedLocationId?: (selectedLocationId: string | null) => void;
    siteSetupDate: string;
}) => {
    const [internalSelectedLocationId, setInternalSelectedLocationId] = useState<string | null>(null);

    const selectedLocationId =
        externalSelectedLocationId !== undefined ? externalSelectedLocationId : internalSelectedLocationId;
    const setSelectedLocationId = externalSetSelectedLocationId || setInternalSelectedLocationId;

    const weekdateBeforeNextSetup = getWeekdateBeforeNextSetup({
        deliveryDays: site.deliveryDays,
        isOpenOnWeekend: site.isOpenOnWeekend,
        startDate: dateFromString(inventoryDate, true),
    });

    const inventoryInstructionTypes = columnSetup.instructions
        .filter(({ type }) =>
            [
                SetupInstructionType.InventoryComplete,
                SetupInstructionType.InventoryCutlery,
                SetupInstructionType.InventoryDry,
                SetupInstructionType.InventorySmallExpiryDate,
                SetupInstructionType.InventoryZero,
                SetupInstructionType.RemoveProducts,
                SetupInstructionType.HotDrinks,
            ].includes(type),
        )
        .map(({ type }) => type);

    if (!inventoryInstructionTypes.length) {
        return null;
    }

    return (
        <Container>
            <Title>
                {inventoryInstructionTypes.reduce(
                    (acc, inventoryType, index) =>
                        acc + (index !== 0 ? ' + ' : '') + SETUP_INSTRUCTION_TYPE_LABELS[inventoryType],
                    '',
                )}
            </Title>
            <Content>
                <ColumnInventoryContainer>
                    <InventoryColumnDisplay
                        column={column}
                        columnInventory={columnInventory}
                        columnSetup={columnSetup}
                        setSelectedLocationId={setSelectedLocationId}
                        selectedLocationId={selectedLocationId}
                        weekdateBeforeNextSetup={weekdateBeforeNextSetup}
                        removedProducts={removeProductsInstruction?.removedProducts}
                        hotDrinksInstruction={hotDrinksInstruction}
                    />
                    <LocationDetailsSection
                        columnSetup={columnSetup}
                        selectedLocationId={selectedLocationId}
                        products={products}
                        site={site}
                        siteSetupDate={siteSetupDate}
                    />
                </ColumnInventoryContainer>
                <InventoryProductsSection
                    checkedLocations={columnInventory.checkedLocations}
                    products={products}
                    setSelectedLocationId={setSelectedLocationId}
                    selectedLocationId={selectedLocationId}
                    weekdateBeforeNextSetup={weekdateBeforeNextSetup}
                    removedProducts={removeProductsInstruction?.removedProducts}
                    hotDrinksInstruction={hotDrinksInstruction}
                    inventoryDate={inventoryDate}
                />
            </Content>
        </Container>
    );
};

const Container = styled.div``;

const Content = styled.div`
    display: flex;
    margin-top: 20px;
`;

const Title = styled.div`
    font-size: 18px;
    font-weight: bold;
    text-align: center;
`;

const ColumnInventoryContainer = styled.div`
    width: 30%;
`;
